import {
  deepFlatChildren,
  getRgObjectsOfType,
  RgDataSetID,
  RG_TYPE,
  RougeDataSets,
  type Apartment,
  type Module,
} from "@project-rouge/rg-core";

// Data + helpers for the Module Explorer admin screen. Kept in a sibling file
// (like cost-tracking-variants.ts) so the screen stays presentational.

export type ModuleEntry = {
  datasetId: RgDataSetID;
  apartmentName: string;
  category: string;
  /** index of this module within its parent apartment */
  moduleIndex: number;
  module: Module;
};

// Flatten a dataset's apartments into one module-per-entry catalogue.
export function buildModuleCatalog(id: RgDataSetID): ModuleEntry[] {
  const entries: ModuleEntry[] = [];
  for (const apartment of RougeDataSets[id].apartments.all) {
    const modules = getRgObjectsOfType<Module>(apartment, RG_TYPE.Module);
    modules.forEach((module, moduleIndex) => {
      entries.push({
        datasetId: id,
        apartmentName: apartment.name,
        category: apartment.data.category,
        moduleIndex,
        module,
      });
    });
  }
  return entries;
}

export type ModuleMeta = {
  /** metres */
  width: number;
  height: number;
  depth: number;
  wallCount: number;
  doorCount: number;
  windowCount: number;
  beamCount: number;
  columnCount: number;
  /** tally of child `modulousId` category prefixes, e.g. { WPA: 4, DCD: 1 } */
  parts: Record<string, number>;
};

export function getModuleMeta(module: Module): ModuleMeta {
  const parts: Record<string, number> = {};
  for (const child of deepFlatChildren(module)) {
    if (!child.modulousId) continue;
    const category = child.modulousId.split("|")[0];
    if (!category) continue;
    parts[category] = (parts[category] ?? 0) + 1;
  }
  return {
    width: module.size[0],
    height: module.size[1],
    depth: module.size[2],
    wallCount: getRgObjectsOfType(module, RG_TYPE.Wall).length,
    doorCount: getRgObjectsOfType(module, RG_TYPE.Door).length,
    windowCount: getRgObjectsOfType(module, RG_TYPE.Window).length,
    beamCount: getRgObjectsOfType(module, RG_TYPE.Beam).length,
    columnCount: getRgObjectsOfType(module, RG_TYPE.Column).length,
    parts,
  };
}

// Count modules per apartment level in a generated world. Reads
// apartment.data.level directly so it doesn't depend on parent pointers
// (which the deserialised world may not carry).
export function modulesPerLevel(apartments: readonly Apartment[]): Record<number, number> {
  const perLevel: Record<number, number> = {};
  for (const apartment of apartments) {
    const count = getRgObjectsOfType(apartment, RG_TYPE.Module).length;
    perLevel[apartment.data.level] = (perLevel[apartment.data.level] ?? 0) + count;
  }
  return perLevel;
}

// A single box footprint that aggregates cleanly. Mirrors the "Medium · Mid"
// COST_TRACKING_VARIANTS entry and uses the app-default dataset (uk_01) via
// CreateBoxWorld — the same proven generator path cost-tracking uses.
// (uk_02 does not aggregate from this box+default-mix path; left as follow-up.)
export const TESSELLATION_EXAMPLE = {
  datasetId: RgDataSetID.uk_01,
  length: 35,
  width: 35,
  floors: 5,
} as const;
